// Kinio 雲端錄影（docs/KINIO-API.md §6 / ADR-003）。
//
//   GET …/recordings?from=<µs>&to=<µs>&limit=&cursor=&has_event= → {"recordings":[clip…], "next_cursor"?}
//                                                           （overlap 查詢、時間升冪；limit 預設 100、上限 200；沒有下一頁不帶 next_cursor）
//   GET …/recordings/{clip_id}/url                        → {url,thumb_url,container,expires_in_seconds}
//   GET …/recordings/playlist.m3u8?from&to                → **server 組好的 VOD m3u8**
//   GET …/recordings/timeline?day=YYYY-MM-DD&tz=…         → 96 個 15 分鐘 bucket
//   GET /v1/homes/{h}/recording                           → {enabled,retention_days,mode_max,…}（唯讀；開關在每台）
//
// server 直接給 playlist（EXT-X-DISCONTINUITY / EXT-X-PROGRAM-DATE-TIME / ENDLIST 都在裡面），hls.js 直接吃。
// playlist 要 Bearer，hls.js 不方便加 header → 我們自己抓成文字再包成 blob: URL 餵它；
// 裡面的段 URL 是「不需要 Bearer 的簽名 download proxy」所以 blob 化後照樣能載。

import { kinioFetch, kinioFetchText } from './auth';
import { DEV_PROXY_API_PREFIX, kinioConfig, planeBase } from './config';

export interface RecordingClip {
  clip_id: string;
  /** µs epoch（媒體時間軸） */
  clip_start_ts_us: number;
  clip_end_ts_us: number;
  duration_ms: number;
  container: 'ts' | 'fmp4' | string;
  quality?: string;
  has_event?: boolean;
  event_ids?: string[];
  size_bytes?: number;
  /** 多鏡頭設備的鏡頭（0 可省略）。 */
  lens?: number;
  /** 5 分鐘有效；web 拿到的是同源 download proxy（不需 Bearer）。 */
  url?: string;
  [k: string]: unknown;
}

export interface TimelineBucket {
  /** RFC3339（帶 tz offset） */
  start: string;
  clips: number;
  events: number;
  seconds: number;
}

export interface RecordingTimeline {
  day: string;
  tz: string;
  bucket_minutes: number;
  buckets: TimelineBucket[];
  retention_days?: number;
}

export interface HomeRecordingSettings {
  enabled: boolean;
  retention_days: number;
  mode_max: 'event' | 'continuous' | string;
}

/** 一天 96 格（15 分鐘）。 */
export const BUCKETS_PER_DAY = 96;
export const BUCKET_MINUTES = 15;

function recPath(apiEndpoint: string, homeId: string, aid: string): string {
  return `${planeBase(apiEndpoint)}/v1/homes/${encodeURIComponent(homeId)}/things/${encodeURIComponent(aid)}/recordings`;
}

export const RECORDINGS_MAX_LIMIT = 200;
/** 一個時間範圍翻頁的上限（每頁 200 → 最多 12 000 段；一天 15 秒一段 = 5 760 段）。 */
export const RECORDINGS_RANGE_MAX_PAGES = 60;

export interface ListRecordingsOptions {
  /** µs epoch */
  fromUs: number;
  toUs: number;
  limit?: number;
  /** 上一頁的 next_cursor。 */
  cursor?: string;
  hasEvent?: boolean;
  /** 多鏡頭（0–7，沒帶 = 0 主鏡頭；plan SPEC #multi-lens-recording）。 */
  lens?: number;
  signal?: AbortSignal;
}

/** 舊名。 */
export type RecordingsPage = RecordingPage;

/** 一頁錄影（時間升冪）；`nextCursor` 沒有 = 到底。 */
export interface RecordingPage {
  recordings: RecordingClip[];
  nextCursor?: string;
  /** 同 nextCursor（wire 名；舊呼叫端用）。 */
  next_cursor?: string;
}

/** GET …/recordings（overlap：`end ≥ from && start ≤ to`，時間升冪）→ 一頁。 */
export async function listRecordings(apiEndpoint: string, homeId: string, aid: string, opts: ListRecordingsOptions): Promise<RecordingPage> {
  const q = new URLSearchParams({ from: String(Math.floor(opts.fromUs)), to: String(Math.floor(opts.toUs)) });
  if (opts.limit) q.set('limit', String(Math.min(Math.max(opts.limit, 1), RECORDINGS_MAX_LIMIT)));
  if (opts.cursor) q.set('cursor', opts.cursor);
  if (opts.hasEvent !== undefined) q.set('has_event', String(opts.hasEvent));
  if (opts.lens) q.set('lens', String(opts.lens));
  const res = await kinioFetch<{ recordings?: RecordingClip[]; next_cursor?: string }>(`${recPath(apiEndpoint, homeId, aid)}?${q}`, { signal: opts.signal });
  return {
    recordings: Array.isArray(res?.recordings) ? res.recordings : [],
    ...(typeof res?.next_cursor === 'string' && res.next_cursor ? { nextCursor: res.next_cursor, next_cursor: res.next_cursor } : {}),
  };
}

/** 範圍內全部錄影：照 next_cursor 翻到底（最多 maxPages 頁；撞到上限 → truncated）。 */
export async function listRecordingsInRange(
  apiEndpoint: string,
  homeId: string,
  aid: string,
  opts: Omit<ListRecordingsOptions, 'cursor' | 'limit'> & { maxPages?: number },
): Promise<{ recordings: RecordingClip[]; truncated: boolean }> {
  const { maxPages = RECORDINGS_RANGE_MAX_PAGES, ...rest } = opts;
  const recordings: RecordingClip[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < maxPages; page++) {
    const res = await listRecordings(apiEndpoint, homeId, aid, { ...rest, limit: RECORDINGS_MAX_LIMIT, cursor });
    recordings.push(...res.recordings);
    if (!res.nextCursor) return { recordings, truncated: false };
    cursor = res.nextCursor;
  }
  return { recordings, truncated: true };
}

/** GET …/recordings/{clip_id}/url → 重新簽名的下載 URL（url 過期時用）。 */
export async function getClipUrl(
  apiEndpoint: string,
  homeId: string,
  aid: string,
  clipId: string,
  signal?: AbortSignal,
  lens?: number,
): Promise<{ url: string; thumb_url?: string; container: string; expires_in_seconds?: number; lens?: number }> {
  const res = await kinioFetch<{ url?: string; thumb_url?: string; container: string; expires_in_seconds?: number; lens?: number }>(
    `${recPath(apiEndpoint, homeId, aid)}/${encodeURIComponent(clipId)}/url${lens ? `?lens=${lens}` : ''}`,
    { signal },
  );
  if (!res?.url) throw new Error('recordings: no url in response');
  return { ...res, url: res.url };
}

/** GET …/recordings/timeline?day=YYYY-MM-DD&tz=… → 96 buckets。 */
export async function getTimeline(
  apiEndpoint: string,
  homeId: string,
  aid: string,
  day: string,
  tz: string = localTimezone(),
  signal?: AbortSignal,
  lens?: number,
): Promise<RecordingTimeline> {
  const q = new URLSearchParams({ day, tz });
  if (lens) q.set('lens', String(lens));
  const res = await kinioFetch<RecordingTimeline>(`${recPath(apiEndpoint, homeId, aid)}/timeline?${q}`, { signal });
  return { ...res, buckets: Array.isArray(res?.buckets) ? res.buckets : [] };
}

/** GET /v1/homes/{h}/recording */
export function getHomeRecording(apiEndpoint: string, homeId: string, signal?: AbortSignal): Promise<HomeRecordingSettings> {
  return kinioFetch<HomeRecordingSettings>(`${planeBase(apiEndpoint)}/v1/homes/${encodeURIComponent(homeId)}/recording`, { signal });
}

// ---- playlist.m3u8 ----

export function playlistUrl(apiEndpoint: string, homeId: string, aid: string, fromUs: number, toUs: number, lens?: number): string {
  const q = new URLSearchParams({ from: String(Math.floor(fromUs)), to: String(Math.floor(toUs)) });
  if (lens) q.set('lens', String(lens));
  return `${recPath(apiEndpoint, homeId, aid)}/playlist.m3u8?${q}`;
}

/**
 * dev proxy 開著時，把 playlist 裡指向 plane api 的絕對段 URL 改成同源 `/kinio-api/<host>/…`。
 *
 * 為什麼需要：dev 的 `/v1/recordings/download` 目前會**同時**回
 * `Access-Control-Allow-Origin: <origin>` 和 `Access-Control-Allow-Origin: *` 兩個標頭，
 * 瀏覽器視為非法（只允許一個值）而擋掉整個跨源請求。走同源代理就繞過這個伺服端 bug。
 * 伺服端修好以後這段就是 no-op（devProxy=false 時本來就不動）。
 */
export function rewritePlaylistForDevProxy(text: string, apiEndpoint: string): string {
  if (!kinioConfig.devProxy) return text;
  let host: string;
  try {
    host = new URL(apiEndpoint).host;
  } catch {
    return text;
  }
  const re = new RegExp(`https?://${host.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')}`, 'g');
  return text.replace(re, `${globalThis.location?.origin ?? ''}${DEV_PROXY_API_PREFIX}/${host}`);
}

/** 抓 playlist 文字（帶 Bearer）。 */
export async function fetchPlaylist(
  apiEndpoint: string,
  homeId: string,
  aid: string,
  fromUs: number,
  toUs: number,
  signal?: AbortSignal,
  lens?: number,
): Promise<string> {
  const text = await kinioFetchText(playlistUrl(apiEndpoint, homeId, aid, fromUs, toUs, lens), { signal });
  return rewritePlaylistForDevProxy(text, apiEndpoint);
}

/** m3u8 有沒有任何一段（沒有 `#EXTINF` = 此區間無錄影）。 */
export function playlistHasSegments(m3u8: string): boolean {
  return /^#EXTINF:/m.test(m3u8);
}

/** playlist 文字 → blob: URL（餵 hls.js）。呼叫端負責 `URL.revokeObjectURL`。 */
export function playlistObjectUrl(m3u8: string): string {
  const blob = new Blob([m3u8], { type: 'application/vnd.apple.mpegurl' });
  return URL.createObjectURL(blob);
}

// ---- 時間輔助（µs 時間軸；瀏覽器本地時區） ----

export function localTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** `YYYY-MM-DD`（本地時區）。 */
export function toDayKey(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** `YYYY-MM-DD` → 當地當天 [00:00, 隔天 00:00) 的 µs 區間。 */
export function dayWindowUs(day: string): { fromUs: number; toUs: number } {
  const [y, m, d] = day.split('-').map((s) => Number(s));
  const start = new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
  const end = new Date(y, (m || 1) - 1, (d || 1) + 1, 0, 0, 0, 0);
  return { fromUs: start.getTime() * 1000, toUs: end.getTime() * 1000 };
}

/** 某個時間點落在當天第幾個 bucket（0..95）；不在當天 → -1。 */
export function bucketIndexOf(day: string, tsMs: number): number {
  const { fromUs, toUs } = dayWindowUs(day);
  const ms = tsMs;
  if (ms < fromUs / 1000 || ms >= toUs / 1000) return -1;
  return Math.floor((ms - fromUs / 1000) / (BUCKET_MINUTES * 60_000));
}

/** bucket 索引 → 當天的起始時間（本地）。 */
export function bucketStartMs(day: string, index: number): number {
  const { fromUs } = dayWindowUs(day);
  return fromUs / 1000 + index * BUCKET_MINUTES * 60_000;
}

/** 96 格的「最忙一格」秒數（畫時間軸高度用；至少 1 避免除以 0）。 */
export function maxBucketSeconds(buckets: TimelineBucket[]): number {
  return Math.max(1, ...buckets.map((b) => b.seconds || 0));
}

export function clipDurationMs(c: RecordingClip): number {
  if (typeof c.duration_ms === 'number' && c.duration_ms > 0) return c.duration_ms;
  return Math.max(0, Math.round((c.clip_end_ts_us - c.clip_start_ts_us) / 1000));
}
